/**
 * 堂票範本的有效期限（前端顯示與到期日預估）。
 *
 * 範本有兩種寫法（2026-10 起）：
 *   - validityMode='days'：領取後 validityDays 天（舊範本沒有 validityMode，一律走這條）
 *   - validityMode='date'：到 validUntil 當天截止，與領取日無關
 *
 * 後端有一份同名的 utils/classTicketExpiry.js 是「真正」算到期日的地方；
 * 這裡只負責結帳預覽、購買同意書變數、清單標籤這些畫面上的呈現，
 * 兩邊規則要一致。
 *
 * 時區一律走 utils/timezone（店家時區），不可直接用 dayjs()。
 */

import { inTz, now, wallTime } from './timezone'

/** 範本實際採用哪種算法；資料不完整時退回 'days'，避免算出 Invalid Date。 */
export function resolveValidityMode(tpl) {
  return tpl?.validityMode === 'date' && (tpl?.validUntilDate || tpl?.validUntil)
    ? 'date'
    : 'days'
}

/**
 * 到期日那一天（'YYYY-MM-DD'）。
 * 後端另外給的 validUntilDate 已經是店家時區的日期字串，優先用它；
 * 只有 validUntil（絕對時刻）時才自己換到店家時區。
 */
function validUntilDayString(tpl) {
  if (tpl?.validUntilDate) return String(tpl.validUntilDate)
  return tpl?.validUntil ? inTz(tpl.validUntil).format('YYYY-MM-DD') : null
}

/**
 * 某張範本若在 issuedAt 當天發放，到期日是哪天。
 * @returns {dayjs.Dayjs|null} 資料不足時回 null（呼叫端自行決定顯示什麼）
 */
export function resolveTemplateExpiry(tpl, issuedAt) {
  if (!tpl) return null
  if (resolveValidityMode(tpl) === 'date') {
    // 'YYYY-MM-DD' 是沒有時區的牆上日期，要用 wallTime 解讀（見 CLAUDE.md 的時區表）
    return wallTime(validUntilDayString(tpl), 'YYYY-MM-DD').endOf('day')
  }
  const days = Number(tpl.validityDays)
  if (!days) return null
  const base = issuedAt
    ? // 純日期字串是牆上日期，其餘（Date／ISO）才是絕對時刻
      /^\d{4}-\d{2}-\d{2}$/.test(String(issuedAt))
      ? wallTime(String(issuedAt), 'YYYY-MM-DD')
      : inTz(issuedAt)
    : now()
  return base.add(days, 'day').endOf('day')
}

/** 到期日字串，給同意書／預覽用（預設 YYYY/MM/DD，與原本的同意書變數一致）。 */
export function formatTemplateExpiry(tpl, issuedAt, pattern = 'YYYY/MM/DD') {
  const d = resolveTemplateExpiry(tpl, issuedAt)
  return d && d.isValid() ? d.format(pattern) : null
}

/** 給人看的有效期限敘述：「領取後 90 天」或「至 2026-12-31」。 */
export function describeValidity(tpl, { short = false } = {}) {
  if (!tpl) return ''
  if (resolveValidityMode(tpl) === 'date') {
    return `${short ? '' : '有效期限 '}至 ${validUntilDayString(tpl)}`
  }
  const days = Number(tpl.validityDays) || 0
  return short ? `${days} 天` : `領取後 ${days} 天`
}

/** 指定到期日是否已過（天數制永遠 false）。用來提醒店家這張範本發出去就是過期票。 */
export function isTemplateExpired(tpl) {
  if (resolveValidityMode(tpl) !== 'date') return false
  return (
    wallTime(validUntilDayString(tpl), 'YYYY-MM-DD').endOf('day').valueOf() <
    now().valueOf()
  )
}

export default {
  resolveValidityMode,
  resolveTemplateExpiry,
  formatTemplateExpiry,
  describeValidity,
  isTemplateExpired
}
